import { useCallback, useMemo, useState } from "react";

import { type DecisionRow } from "../../../types";
import { type IncreaseValues } from "../types";

export const useIncreaseValues = (baseDecisionRowsByManagerId: Record<string, DecisionRow[]>) => {
	const [draftOverrides, setDraftOverrides] = useState<IncreaseValues>({});

	const salaryById = useMemo(() => {
		const map = new Map<string, number>();
		for (const rows of Object.values(baseDecisionRowsByManagerId)) {
			for (const row of rows) {
				map.set(row._id, row.current_salary);
			}
		}
		return map;
	}, [baseDecisionRowsByManagerId]);

	const increaseValues: IncreaseValues = useMemo(() => {
		const merged: IncreaseValues = {};
		for (const rows of Object.values(baseDecisionRowsByManagerId)) {
			for (const row of rows) {
				if (draftOverrides[row._id]) {
					merged[row._id] = draftOverrides[row._id];
				} else {
					merged[row._id] = {
						percent: row.proposed_increase_percent ?? null,
						amount: row.proposed_increase_amount ?? null,
					};
				}
			}
		}
		return merged;
	}, [draftOverrides, baseDecisionRowsByManagerId]);

	const onPercentChange = useCallback(
		(id: string, percent: number | "") => {
			setDraftOverrides((prev) => {
				if (percent === "") return { ...prev, [id]: { percent: "", amount: "" } };
				const salary = salaryById.get(id) ?? 0;
				const amount = Math.round(salary * (percent / 100) * 100) / 100;
				return { ...prev, [id]: { percent, amount } };
			});
		},
		[salaryById]
	);

	const onAmountChange = useCallback(
		(id: string, amount: number | "") => {
			setDraftOverrides((prev) => {
				if (amount === "") return { ...prev, [id]: { percent: "", amount: "" } };
				const salary = salaryById.get(id) ?? 0;
				const percent = salary > 0 ? Math.round((amount / salary) * 100 * 100) / 100 : 0;
				return { ...prev, [id]: { percent, amount } };
			});
		},
		[salaryById]
	);

	return { increaseValues, setDraftOverrides, onPercentChange, onAmountChange };
};
